import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { Header } from '@components/Header';
import { PagePagination } from '@components/core-ui/Pagination';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { Tabs } from '@pages/Admin/TransactionsPage/components/Tabs';
import { TransactionType } from '@pages/User/BillingAccountDetailsPage/components/TransactionType';
import { useAPI } from '@providers/useAPI';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { transactionsKeys, TTransactionCodesListParams } from 'app/queries/transactions/transactionsKeys';
import { useMemo } from 'react';
import { createFetchTransactionCodesQueryFn } from '../../api/fetchTransactionCodes';

import { CellContent } from './styles/CellContent';
import { MainContent } from './styles/MainContent';

const SEARCH_DEBOUNCE_MS = 300;

export const Content = () => {
  const { call } = useAPI();
  const { page, pageSize, search, orderBy, orderType, setSearch, setPage, setPageSize } = useListQueryParams({
    defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
  });
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const listParams = useMemo<TTransactionCodesListParams>(
    () => ({ page, pageSize, orderBy, orderType, search: debouncedSearch }),
    [debouncedSearch, orderBy, orderType, page, pageSize],
  );

  const codesQuery = useQuery({
    queryKey: transactionsKeys.codeList(listParams),
    queryFn: createFetchTransactionCodesQueryFn(call),
    placeholderData: keepPreviousData,
    enabled: !isSearchSettling,
  });
  const transactionCodes = codesQuery.data ?? { data: [], count: 0 };
  const isLoading = codesQuery.isFetching || isSearchSettling;

  return (
    <>
      <Header
        title="Transactions"
        setSearch={setSearch}
        search={search}
        leftSideContent={[<Tabs key="TransactionsTabs" currentTab="Transaction Codes" />]}
      />

      <LinearProgressLoader active={isLoading} />
      <MainContent>
        <TableWrapper>
          <Table>
            <Thead>
              <Tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Description</Th>
                <Th>Type</Th>
              </Tr>
            </Thead>
            <Tbody>
              {transactionCodes.data?.map((data) => (
                <Tr key={data.code}>
                  <Td>
                    <CellContent>{data.code}</CellContent>
                  </Td>
                  <Td>
                    <CellContent>{data.name}</CellContent>
                  </Td>
                  <Td>
                    <CellContent>
                      <div style={{ whiteSpace: 'normal' }}>{data.description}</div>
                    </CellContent>
                  </Td>
                  <Td>
                    <CellContent>
                      <TransactionType type={data.status} />
                    </CellContent>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableWrapper>
        <PagePagination
          totalItemsCount={transactionCodes.count ?? 0}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </MainContent>
    </>
  );
};
